<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <button class="header-btn" @click="close">취소</button>
        <h2>{{ isEdit ? '정보 수정' : '새 게시물' }}</h2>
        <button 
          class="header-btn primary" 
          @click="submit" 
          :disabled="!isValid"
        >
          {{ isEdit ? '완료' : '공유' }}
        </button>
      </div>

      <!-- Content -->
      <div class="edit-content">
        <!-- Image Section -->
        <div class="image-section">
          <!-- Image Preview -->
          <div class="image-preview" v-if="form.images.length > 0">
            <div class="preview-carousel">
              <div 
                class="preview-track" 
                :style="{ transform: `translateX(-${previewIndex * 100}%)` }"
              >
                <div class="preview-slide" v-for="(img, idx) in form.images" :key="idx">
                  <img :src="img" alt="미리보기" @click="openViewer(idx)" />
                  <button class="remove-btn" @click="removeImage(idx)">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Preview Indicators -->
            <div class="preview-indicators" v-if="form.images.length > 1">
              <span 
                v-for="(_, idx) in form.images" 
                :key="idx"
                :class="['indicator', { active: previewIndex === idx }]"
                @click="previewIndex = idx"
              ></span>
            </div>

            <!-- Navigation Arrows -->
            <button 
              class="nav-arrow prev" 
              v-if="form.images.length > 1 && previewIndex > 0"
              @click="previewIndex--"
            >
              <i class="ph ph-caret-left"></i>
            </button>
            <button 
              class="nav-arrow next" 
              v-if="form.images.length > 1 && previewIndex < form.images.length - 1"
              @click="previewIndex++"
            >
              <i class="ph ph-caret-right"></i>
            </button>

            <!-- Add More Button -->
            <button class="add-more-btn" @click="triggerFileInput" :disabled="uploading">
              <i class="ph" :class="uploading ? 'ph-spinner' : 'ph-plus'"></i>
            </button>
          </div>

          <!-- Empty Image Upload -->
          <div class="image-upload" v-else @click="triggerFileInput">
            <div class="upload-content" :class="{ uploading }">
              <i class="ph" :class="uploading ? 'ph-spinner' : 'ph-image'"></i>
              <h3 v-if="!uploading">사진을 여기에 추가</h3>
              <p v-if="!uploading">클릭하여 사진 선택</p>
              <p v-else>업로드 중...</p>
            </div>
          </div>

          <input 
            type="file" 
            ref="fileInput"
            accept="image/*"
            multiple
            @change="handleFileSelect"
            style="display: none;"
          />
        </div>

        <!-- Thumbnail List (순서 변경용) -->
        <div class="thumbnail-section" v-if="form.images.length > 1">
          <div class="thumbnail-header">
            <span>순서 변경</span>
            <span class="hint">길게 눌러 드래그</span>
          </div>
          <div class="thumbnail-list" ref="thumbnailList">
            <div 
              v-for="(img, idx) in form.images" 
              :key="img"
              class="thumbnail-item"
              :class="{ dragging: dragIndex === idx, dragover: dragOverIndex === idx }"
              draggable="true"
              @dragstart="onDragStart($event, idx)"
              @dragend="onDragEnd"
              @dragover.prevent="onDragOver($event, idx)"
              @drop.prevent="onDrop(idx)"
              @touchstart="onTouchStart($event, idx)"
              @touchmove="onTouchMove"
              @touchend="onTouchEnd"
              @click="previewIndex = idx"
            >
              <img :src="img" alt="썸네일" />
              <span class="thumb-number">{{ idx + 1 }}</span>
            </div>
          </div>
        </div>

        <!-- Caption Section -->
        <div class="caption-section">
          <div class="caption-header">
            <div class="user-badge">
              <div class="badge-avatar">
                <i class="ph-fill ph-user"></i>
              </div>
              <span class="badge-name">{{ mb_id }}</span>
            </div>
          </div>

          <input 
            type="text"
            class="title-input"
            v-model="form.title" 
            placeholder="제목을 입력하세요"
          />

          <textarea 
            class="caption-input"
            v-model="form.content" 
            placeholder="문구를 작성하세요..."
            rows="4"
          ></textarea>
        </div>

        <!-- Notice -->
        <div class="notice">
          <i class="ph ph-info"></i>
          <span>개인정보는 블러 처리 후 올려주세요</span>
        </div>
      </div>
    </div>

    <!-- Image Viewer Modal -->
    <div class="image-viewer" v-if="showViewer" @click="showViewer = false">
      <button class="viewer-close">
        <i class="ph ph-x"></i>
      </button>
      <div class="viewer-content" @click.stop>
        <img :src="form.images[viewerIndex]" alt="이미지" />
      </div>
      <div class="viewer-indicators" v-if="form.images.length > 1">
        <span 
          v-for="(_, idx) in form.images" 
          :key="idx"
          :class="['indicator', { active: viewerIndex === idx }]"
          @click.stop="viewerIndex = idx"
        ></span>
      </div>
      <button 
        class="viewer-arrow prev" 
        v-if="form.images.length > 1 && viewerIndex > 0"
        @click.stop="viewerIndex--"
      >
        <i class="ph ph-caret-left"></i>
      </button>
      <button 
        class="viewer-arrow next" 
        v-if="form.images.length > 1 && viewerIndex < form.images.length - 1"
        @click.stop="viewerIndex++"
      >
        <i class="ph ph-caret-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted } from "vue"

export default {
  props: {
    oth_showSnsEdit: Function,
    editIdx: { default: null }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const fileInput = ref(null)
    const uploading = ref(false)
    const previewIndex = ref(0)
    const isEdit = computed(() => props.editIdx !== null)
    
    // 이미지 뷰어
    const showViewer = ref(false)
    const viewerIndex = ref(0)
    
    // 드래그앤드롭
    const thumbnailList = ref(null)
    const dragIndex = ref(null)
    const dragOverIndex = ref(null)
    
    // 터치 드래그
    const touchStartY = ref(0)
    const touchStartX = ref(0)
    const touchDragIndex = ref(null)
    // const touchClone = ref(null)

    const form = ref({
      title: '',
      content: '',
      images: []
    })
    
    // 원본 데이터 (변경 여부 체크용)
    const originalForm = ref({
      title: '',
      content: '',
      images: []
    })

    const isValid = computed(() => {
      return form.value.title.trim() && form.value.content.trim()
    })

    const loadPost = async () => {
      if (!props.editIdx) return
      
      const rows = await sql_query(`
        SELECT title, content, images
        FROM sns_post
        WHERE idx = ${props.editIdx} AND mb_id = '${mb_id}'
      `)
      
      if (rows.length > 0) {
        const data = {
          title: rows[0].title,
          content: rows[0].content,
          images: rows[0].images ? JSON.parse(rows[0].images) : []
        }
        form.value = { ...data, images: [...data.images] }
        originalForm.value = { ...data, images: [...data.images] }
      }
    }

    const triggerFileInput = () => {
      if (uploading.value) return
      fileInput.value.click()
    }

    const handleFileSelect = async (e) => {
      const files = e.target.files
      if (!files.length) return

      uploading.value = true

      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          alert('파일 크기는 5MB 이하만 가능합니다')
          continue
        }

        const formData = new FormData()
        formData.append('idx', props.editIdx || '')
        formData.append('table', 'sns_post')
        formData.append('usage', 'image')
        formData.append('user', mb_id)
        formData.append('file', file)

        try {
          const response = await fetch('/api/upload/image', {
            method: 'POST',
            body: formData,
            mode: 'cors',
            credentials: 'include'
          })
          const data = await response.json()

          if (data.url) {
            let imageUrl = data.url
            if (imageUrl.startsWith('/')) {
              imageUrl = window.location.origin + imageUrl
            }
            form.value.images.push(imageUrl)
            previewIndex.value = form.value.images.length - 1
          } else {
            alert('이미지 업로드 실패')
          }
        } catch (err) {
          console.error('업로드 오류:', err)
          alert('업로드 중 오류가 발생했습니다')
        }
      }

      uploading.value = false
      e.target.value = ''
    }

    const removeImage = (idx) => {
      form.value.images.splice(idx, 1)
      if (previewIndex.value >= form.value.images.length) {
        previewIndex.value = Math.max(0, form.value.images.length - 1)
      }
    }
    
    // 이미지 뷰어
    const openViewer = (idx) => {
      viewerIndex.value = idx
      showViewer.value = true
    }
    
    // 드래그앤드롭 (PC)
    const onDragStart = (e, idx) => {
      dragIndex.value = idx
      e.dataTransfer.effectAllowed = 'move'
    }
    
    const onDragEnd = () => {
      dragIndex.value = null
      dragOverIndex.value = null
    }
    
    const onDragOver = (e, idx) => {
      if (dragIndex.value === null || dragIndex.value === idx) return
      dragOverIndex.value = idx
    }
    
    const onDrop = (idx) => {
      if (dragIndex.value === null || dragIndex.value === idx) return
      
      const images = [...form.value.images]
      const [removed] = images.splice(dragIndex.value, 1)
      images.splice(idx, 0, removed)
      form.value.images = images
      
      // 현재 보고 있던 이미지 추적
      if (previewIndex.value === dragIndex.value) {
        previewIndex.value = idx
      } else if (previewIndex.value > dragIndex.value && previewIndex.value <= idx) {
        previewIndex.value--
      } else if (previewIndex.value < dragIndex.value && previewIndex.value >= idx) {
        previewIndex.value++
      }
      
      dragIndex.value = null
      dragOverIndex.value = null
    }
    
    // 터치 드래그 (모바일)
    const onTouchStart = (e, idx) => {
      const touch = e.touches[0]
      touchStartX.value = touch.clientX
      touchStartY.value = touch.clientY
      touchDragIndex.value = idx
    }
    
    const onTouchMove = (e) => {
      if (touchDragIndex.value === null) return
      
      const touch = e.touches[0]
      const diffX = Math.abs(touch.clientX - touchStartX.value)
      const diffY = Math.abs(touch.clientY - touchStartY.value)
      
      // 수평 이동이 더 크면 드래그로 판단
      if (diffX > 20 && diffX > diffY) {
        e.preventDefault()
        
        // 드래그 대상 찾기
        const elements = thumbnailList.value.querySelectorAll('.thumbnail-item')
        for (let i = 0; i < elements.length; i++) {
          const rect = elements[i].getBoundingClientRect()
          if (touch.clientX >= rect.left && touch.clientX <= rect.right) {
            dragOverIndex.value = i
            break
          }
        }
      }
    }
    
    const onTouchEnd = () => {
      if (touchDragIndex.value !== null && dragOverIndex.value !== null && touchDragIndex.value !== dragOverIndex.value) {
        const images = [...form.value.images]
        const [removed] = images.splice(touchDragIndex.value, 1)
        images.splice(dragOverIndex.value, 0, removed)
        form.value.images = images
        
        if (previewIndex.value === touchDragIndex.value) {
          previewIndex.value = dragOverIndex.value
        }
      }
      
      touchDragIndex.value = null
      dragOverIndex.value = null
    }

    const submit = async () => {
      if (!isValid.value) return

      const imagesJson = form.value.images.length > 0 
        ? JSON.stringify(form.value.images) 
        : null

      if (isEdit.value) {
        await sql_query(`
          UPDATE sns_post SET
            title = '${form.value.title.replace(/'/g, "''")}',
            content = '${form.value.content.replace(/'/g, "''")}',
            images = ${imagesJson ? `'${imagesJson.replace(/'/g, "''")}'` : 'NULL'}
          WHERE idx = ${props.editIdx} AND mb_id = '${mb_id}'
        `)
      } else {
        await sql_query(`
          INSERT INTO sns_post (mb_id, title, content, images)
          VALUES (
            '${mb_id}',
            '${form.value.title.replace(/'/g, "''")}',
            '${form.value.content.replace(/'/g, "''")}',
            ${imagesJson ? `'${imagesJson.replace(/'/g, "''")}'` : 'NULL'}
          )
        `)
      }

      props.oth_showSnsEdit(false, { refresh: true })
    }

    // 변경사항 있는지 체크
    const hasChanges = () => {
      if (form.value.title !== originalForm.value.title) return true
      if (form.value.content !== originalForm.value.content) return true
      if (form.value.images.length !== originalForm.value.images.length) return true
      if (JSON.stringify(form.value.images) !== JSON.stringify(originalForm.value.images)) return true
      return false
    }

    const close = () => {
      if (hasChanges()) {
        if (!confirm('작성 중인 내용이 있습니다. 정말 나가시겠어요?')) return
      }
      props.oth_showSnsEdit(false)
    }

    onMounted(() => {
      loadPost()
    })

    return {
      mb_id,
      fileInput,
      uploading,
      previewIndex,
      isEdit,
      form,
      isValid,
      showViewer,
      viewerIndex,
      thumbnailList,
      dragIndex,
      dragOverIndex,
      triggerFileInput,
      handleFileSelect,
      removeImage,
      openViewer,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDrop,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 90%;
  max-width: 470px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header > h2 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.header-btn {
  border: none;
  background: none;
  padding: 0;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  cursor: pointer;
}

.header-btn.primary {
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
}

.header-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

/* Content */
.edit-content {
  flex: 1;
  overflow-y: auto;
}

/* Image Section */
.image-section {
  position: relative;
}

.image-preview {
  position: relative;
  background: var(--color-background-light);
}

.preview-carousel {
  width: 100%;
  overflow: hidden;
}

.preview-track {
  display: flex;
  transition: transform 0.3s ease;
}

.preview-slide {
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 1;
  position: relative;
}

.preview-slide > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.remove-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn > i {
  font-size: 16px;
  color: #fff;
}

.preview-indicators {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.indicator.active {
  background: #fff;
}

/* Navigation Arrows */
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.nav-arrow.prev { left: 12px; }
.nav-arrow.next { right: 12px; }

.nav-arrow > i {
  font-size: 16px;
  color: #333;
}

.add-more-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.add-more-btn > i {
  font-size: 20px;
  color: #fff;
}

.add-more-btn > i.ph-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Image Upload */
.image-upload {
  aspect-ratio: 1;
  background: var(--color-background-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--color-text-secondary);
}

.upload-content > i {
  font-size: 64px;
}

.upload-content.uploading > i {
  animation: spin 1s linear infinite;
}

.upload-content > h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-normal);
  color: var(--color-text-primary);
  margin: 0;
}

.upload-content > p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
}

/* Thumbnail Section */
.thumbnail-section {
  padding: 16px;
  border-bottom: 1px solid var(--color-background-light);
}

.thumbnail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.thumbnail-header > span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.thumbnail-header > .hint {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-normal);
  color: var(--color-text-third);
}

.thumbnail-list {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.thumbnail-list::-webkit-scrollbar {
  height: 4px;
}

.thumbnail-list::-webkit-scrollbar-thumb {
  background: var(--color-background-light);
  border-radius: 2px;
}

.thumbnail-item {
  position: relative;
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  cursor: grab;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.thumbnail-item:active {
  cursor: grabbing;
}

.thumbnail-item > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.thumbnail-item.dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.thumbnail-item.dragover {
  border-color: var(--color-brand);
  transform: scale(1.05);
}

.thumb-number {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 18px;
  height: 18px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Caption Section */
.caption-section {
  padding: 16px;
}

.caption-header {
  margin-bottom: 16px;
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.badge-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #833AB4, #FD1D1D, #F77737);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
}

.badge-avatar > i {
  width: 100%;
  height: 100%;
  background: var(--color-background);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--color-text-secondary);
}

.badge-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.title-input {
  width: 100%;
  padding: 12px 0;
  border: none;
  border-bottom: 1px solid var(--color-background-light);
  background: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  outline: none;
  box-sizing: border-box;
}

.title-input::placeholder {
  color: var(--color-text-third);
  font-weight: var(--font-weight-normal);
}

.caption-input {
  width: 100%;
  padding: 12px 0;
  border: none;
  background: none;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  outline: none;
  resize: none;
  line-height: 1.6;
  box-sizing: border-box;
}

.caption-input::placeholder {
  color: var(--color-text-third);
}

/* Notice */
.notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  margin: 16px;
  background: var(--color-brand-light);
  border-radius: var(--radius-md);
}

.notice > i {
  font-size: 18px;
  color: var(--color-brand);
}

.notice > span {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

/* Image Viewer */
.image-viewer {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.viewer-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-close > i {
  font-size: 28px;
  color: #fff;
}

.viewer-content {
  max-width: 90%;
  max-height: 90%;
}

.viewer-content > img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

.viewer-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-arrow.prev { left: 16px; }
.viewer-arrow.next { right: 16px; }

.viewer-arrow > i {
  font-size: 24px;
  color: #fff;
}

.viewer-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
}

.viewer-indicators > .indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.viewer-indicators > .indicator.active {
  background: #fff;
}
</style>